// docker/nginx.conf serves exactly these paths framable by Slack; keep both regexes identical.
export const SLACK_EMBED_PATH_REGEX =
  /^\/embed\/(?:o\/[^/]+\/org\/[^/]+|[^/]+\/[^/]+)\/reports\/[^/]+\/version\/[^/]+\/widgets\/[^/]+\/?$/;

const ENCODED_ASCII_REGEX = /%([0-7][0-9a-f])/gi;

export const SLACK_EMBED_RENDER_QUERY_PARAM = 'render';
export const SLACK_EMBED_RENDER_QUERY_VALUE = 'png';

const SLACK_EMBED_CODE_FRAGMENT_PARAM = 'code';

const ENTERPRISE_TARGET_REGEX =
  /^\/embed\/o\/([^/]+)\/org\/([^/]+)\/reports\/([^/]+)\/version\/([^/]+)\/widgets\/([^/]+)\/?$/;
const PLAIN_TARGET_REGEX =
  /^\/embed\/([^/]+)\/([^/]+)\/reports\/([^/]+)\/version\/([^/]+)\/widgets\/([^/]+)\/?$/;

export type SlackEmbedTarget = {
  organizationName: string;
  projectName: string;
  reportId: string;
  versionId: string;
  widgetId: string;
};

/**
 * True exactly for the paths that nginx serves framable by Slack, so the desktop app never boots
 * on a framable page and every other path (for example any page of a workspace named `embed`)
 * boots the desktop app. nginx matches locations against the decoded path with merged slashes and
 * resolved dot segments, so the same normalization is applied before the check.
 */
export function isSlackEmbedPathname(pathname: string): boolean {
  return SLACK_EMBED_PATH_REGEX.test(normalizePathname(pathname));
}

export function isSlackEmbedRenderMode(search: string): boolean {
  return (
    new URLSearchParams(search).get(SLACK_EMBED_RENDER_QUERY_PARAM) ===
    SLACK_EMBED_RENDER_QUERY_VALUE
  );
}

export function parseSlackEmbedTarget(pathname: string): SlackEmbedTarget | undefined {
  if (!SLACK_EMBED_PATH_REGEX.test(pathname)) {
    return undefined;
  }

  const match = ENTERPRISE_TARGET_REGEX.exec(pathname) ?? PLAIN_TARGET_REGEX.exec(pathname);

  if (!match) {
    return undefined;
  }

  const segments = match.slice(1).map(safeDecodeURIComponent);
  const [organizationName, projectName, reportId, versionId, widgetId] = segments;

  if (
    organizationName === undefined ||
    projectName === undefined ||
    reportId === undefined ||
    versionId === undefined ||
    widgetId === undefined
  ) {
    return undefined;
  }

  return { organizationName, projectName, reportId, versionId, widgetId };
}

export type ConsumedSlackEmbedCode = {
  code: string | undefined;
  /** False when the fragment, and so possibly the code, is still in the page URL. */
  urlCleared: boolean;
};

/**
 * Reads the single-use code from the URL fragment and removes the fragment from the URL, so the
 * code is not kept in the browser history or exposed to any later script. Never throws.
 */
export function consumeSlackEmbedCode(
  location: Pick<Location, 'hash' | 'href'>,
  history: Pick<History, 'replaceState' | 'state'>,
): ConsumedSlackEmbedCode {
  if (!location.hash) {
    return { code: undefined, urlCleared: true };
  }

  const code = new URLSearchParams(location.hash.slice(1)).get(SLACK_EMBED_CODE_FRAGMENT_PARAM);

  try {
    // The absolute URL keeps the origin: a relative "//embed/..." would resolve to another host.
    const url = new URL(location.href);
    url.hash = '';
    history.replaceState(history.state, '', url.href);
  } catch {
    // The code is still returned so the embed can start; `urlCleared` stays false, so the caller
    // must not start anything that records the page URL (error reporting, user action tracking).
  }

  return { code: code ? code : undefined, urlCleared: !location.hash };
}

function normalizePathname(pathname: string): string {
  // Only ASCII escapes can change the "/", "." and path regex checks, so other bytes stay encoded.
  const decoded = pathname.replace(ENCODED_ASCII_REGEX, (_match, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
  const rawSegments = decoded.split('/');

  const segments = rawSegments.reduce<string[]>((result, segment) => {
    if (segment === '..') {
      return result.slice(0, -1);
    }

    return segment === '' || segment === '.' ? result : [...result, segment];
  }, []);

  const lastRawSegment = rawSegments[rawSegments.length - 1];
  const trailingSlash =
    segments.length > 0 &&
    (lastRawSegment === '' || lastRawSegment === '.' || lastRawSegment === '..');

  return `/${segments.join('/')}${trailingSlash ? '/' : ''}`;
}

function safeDecodeURIComponent(segment: string): string | undefined {
  try {
    return decodeURIComponent(segment);
  } catch {
    return undefined;
  }
}
